/**
 * TanStack Query 키를 한곳에서 만든다. 뿌리(도메인) 하나 밑에 가지가 달린 계층형이라
 * `qc.invalidateQueries({ queryKey: qk.members.all })` 한 줄이면 목록·상세·통계가 함께 다시 온다.
 * 목록 훅(useList)은 `all` 을 앞머리로 받아 `[...all, params]` 로 키를 만든다.
 */
type Params = unknown;

/** 뿌리 하나와 가장 흔한 가지(목록·상세). 더 필요한 가지는 도메인마다 덧붙인다. */
const domain = <R extends string>(root: R) => ({
  all: [root] as const,
  detail: (id: Params) => [root, "detail", id] as const,
});

export const qk = {
  me: ["me"] as const,
  dashboard: {
    all: ["dashboard"] as const,
    usagePeriod: (period: string) => ["dashboard", "usage-period", period] as const,
  },
  admins: domain("admins"),
  adminEvents: domain("admin-events"),
  appContent: (key: string) => ["app-content", key] as const,
  apartments: {
    ...domain("apartments"),
    regions: ["apartments", "regions"] as const,
    stations: ["apartments", "station"] as const,
    station: (sid: string | null) => ["apartments", "station", sid] as const,
  },
  articles: domain("articles"),
  asArticles: { ...domain("as-articles"), stats: (params: Params) => ["as-articles", "stats", params] as const },
  cardStock: domain("card-stock"),
  carModels: {
    ...domain("car-models"),
    stats: ["car-models", "stats"] as const,
    catalog: ["car-models", "catalog"] as const,
    detail: (id: number | null) => ["car-models", "detail", id] as const,
  },
  chargers: {
    ...domain("chargers"),
    nextCid: (station: string | null, count: number) => ["chargers", "next-cid", station, count] as const,
    sitePhotos: (id: string | null) => ["chargers", "site-photos", id] as const,
    connectionHistory: (id: string) => ["chargers", "connection-history", id] as const,
    lastSession: (id: string) => ["chargers", "last-session", id] as const,
  },
  corps: { ...domain("corps"), map: ["corps", "map"] as const },
  cs: { ...domain("cs-articles"), stats: (params: Params) => ["cs-articles", "stats", params] as const },
  couponPoint: {
    policies: ["cp-policies"] as const,
    coupons: domain("cp-coupons"),
    points: { all: ["cp-points"] as const, page: (...args: Params[]) => ["cp-points", ...args] as const },
  },
  sessions: {
    ...domain("charge-sessions"),
    stats: (params: Params) => ["charge-sessions", "stats", params] as const,
    breakdown: (id: number | null) => ["charge-sessions", "breakdown", id] as const,
    live: ["charge-sessions", "live"] as const,
  },
  errorCodes: {
    ...domain("error-codes"),
    nextAlias: (maker: Params, category: Params) => ["error-codes", "next-alias", maker, category] as const,
  },
  faults: domain("fault-histories"),
  certRecords: domain("cert-records"),
  monitoring: {
    all: ["monitoring"] as const,
    summary: ["monitoring", "summary"] as const,
    stations: ["monitoring", "stations"] as const,
    chargers: { all: ["monitoring", "chargers"] as const, of: (sid: string | null) => ["monitoring", "chargers", sid] as const },
  },
  makers: domain("makers"),
  models: { ...domain("chrgr-models"), files: (id: string | null) => ["chrgr-models", "files", id] as const },
  payments: {
    outstanding: { all: ["payments", "outstanding"] as const, page: (page: number) => ["payments", "outstanding", page] as const },
    attempts: { all: ["payments", "attempts"] as const, of: (elctcId: Params) => ["payments", "attempts", elctcId] as const },
    unpaidMembers: { all: ["payments", "unpaid-members"] as const, page: (...args: Params[]) => ["payments", "unpaid-members", ...args] as const },
    unpaidMember: (userId: number | null) => ["payments", "unpaid-member", userId] as const,
  },
  members: {
    ...domain("members"),
    stats: ["members", "stats"] as const,
    activity: ["members", "activity"] as const,
    cardHistory: (id: number, tag: string | null) => ["members", "card-history", id, tag] as const,
    cardUsage: (id: number, tag: string | null) => ["members", "card-usage", id, tag] as const,
    unitHist: (params: Params) => ["members", "unit-hist", params] as const,
  },
  roaming: {
    all: ["roaming"] as const,
    suppliers: ["roaming", "suppliers"] as const,
    prices: ["roaming", "prices"] as const,
    settlement: (ym: string) => ["roaming", "settlement", ym] as const,
    outbound: (ym: string) => ["roaming", "outbound", ym] as const,
  },
  pricing: {
    kindPrices: ["pricing", "kind-prices"] as const,
    history: { all: ["pricing", "history"] as const, of: (kind: string, at: string) => ["pricing", "history", kind, at] as const },
    preview: (p: { kind: string; mem_ty: string; at: string; station_idntfr?: string } | null) =>
      ["pricing", "preview", p?.kind ?? "", p?.mem_ty ?? "", p?.at ?? "", p?.station_idntfr ?? ""] as const,
    fieldValues: ["pricing", "field-values"] as const,
  },
  promotions: {
    ...domain("promotions"),
    fieldReflection: (ids: string) => ["promotions", "field-reflection", ids] as const,
  },
  stations: {
    ...domain("stations"),
    facets: ["stations", "facets"] as const,
    changes: (id: string | null, limit: number) => ["stations", "changes", id, limit] as const,
    sitePhotos: (id: string | null) => ["stations", "site-photos", id] as const,
    kepcoNos: (id: string | null) => ["stations", "kepco-nos", id] as const,
  },
  setupRequests: domain("setup-requests"),
  settlement: {
    all: ["settlement"] as const,
    month: (ym: string) => ["settlement", "month", ym] as const,
    toss: (params: Params) => ["settlement", "toss", params] as const,
    sales: (params: Params) => ["settlement", "sales", params] as const,
    salesStats: (params: Params) => ["settlement", "sales-stats", params] as const,
    series: (params: Params) => ["settlement", "series", params] as const,
    summary: (params: Params) => ["settlement", "summary", params] as const,
    apt: ["settlement", "apt"] as const,
  },
  telegrams: {
    ...domain("telegrams"),
    stats: (params: Params) => ["telegrams", "stats", params] as const,
    payload: (...args: Params[]) => ["telegrams", "payload", ...args] as const,
    pair: (...args: Params[]) => ["telegrams", "pair", ...args] as const,
    near: (...args: Params[]) => ["telegrams", "near", ...args] as const,
  },
  remoteControls: { ...domain("remote-controls"), stats: (params: Params) => ["remote-controls", "stats", params] as const },
  stats: domain("stats"),
  userCards: {
    ...domain("user-cards"),
    issuanceCounts: ["user-cards", "issuance-counts"] as const,
    history: (id: number | null) => ["user-cards", "history", id] as const,
  },
  stipulations: {
    ...domain("stipulations"),
    groups: (params: Params) => ["stipulations", "groups", params] as const,
    agreements: (id: number | null, params: Params) => ["stipulations", "detail", id, "agreements", params] as const,
  },
};
